import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "@/components/ui/breadcrumb"
import { Logout, Magnifer, Maximize, Minimize, QuestionCircle, Settings, User } from "@solar-icons/react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import Avatar from "@/components/core/avatar"
import { useAuthStore } from "@/stores/auth"
import {
  ADMIN_HOME,
  ADMIN_SETTINGS_PROFILE,
} from "@/constants/paths/admin"
import { useSignOut } from "@/hooks/use-sign-out"
import { ConsoleScopeSwitcher } from "@/components/console-scope-switcher"
import { HeaderNotificationsMenu } from "@/components/notifications/header-notifications-menu"
import { LanguageSwitcher } from "@/components/marketing/language-switcher"
import { useBreadcrumbs } from "@/hooks/use-breadcrumbs"
import { useCommandPalette } from "@/components/command-palette"
import { useTranslation } from "react-i18next"

export function AdminHeader() {
  const navigate = useNavigate()
  const { t: tc } = useTranslation("common")
  const { t: ts } = useTranslation("settings")
  const user = useAuthStore((state) => state.user)
  const signOut = useSignOut()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { toggle: openCommandPalette } = useCommandPalette()
  const { items: breadcrumbs } = useBreadcrumbs("admin")

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
        setIsFullscreen(false)
      }
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b bg-background">
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 sm:gap-4 px-2 sm:px-4">
        <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
          <SidebarTrigger className="-ml-1" data-tour-step-id="header-sidebar-trigger" />
          <ConsoleScopeSwitcher />
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            className="hidden h-9 gap-2 sm:flex"
            onClick={openCommandPalette}
            data-tour-step-id="header-search"
          >
            <Magnifer className="h-4 w-4" />
            <span className="text-muted-foreground">{tc("actions.search")}</span>
            <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              ⌘K
            </kbd>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:hidden"
            onClick={openCommandPalette}
            data-tour-step-id="header-search"
          >
            <Magnifer className="h-4 w-4" />
          </Button>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <LanguageSwitcher buttonVariant="secondary" />

            <HeaderNotificationsMenu />

            <Button
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={() => navigate(ADMIN_SETTINGS_PROFILE)}
              data-tour-step-id="header-settings"
            >
              <Settings className="h-4 w-4" />
            </Button>

            <Button
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={toggleFullscreen}
              data-tour-step-id="header-fullscreen"
            >
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="cursor-pointer px-0 rounded-full" data-tour-step-id="header-profile">
                  <Avatar src="" alt={user?.name ?? "Admin"} size="sm" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar src="" alt={user?.name ?? "Admin"} size="sm" />
                    <div className="flex flex-col min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{user?.name ?? "Admin"}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email ?? ""}</p>
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={ADMIN_SETTINGS_PROFILE} className="flex items-center gap-2">
                    <User className="size-4" />
                    <span>{ts("nav.profile")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ADMIN_SETTINGS_PROFILE} className="flex items-center gap-2">
                    <Settings className="size-4" />
                    <span>{tc("nav.settings")}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="mailto:support@forumone.tech" className="flex items-center gap-2">
                    <QuestionCircle className="size-4" />
                    <span>{tc("actions.helpSupport")}</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive">
                  <Logout className="size-4 mr-2" />
                  <span>{tc("actions.signOut")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Separator />

      <div className="flex h-8 shrink-0 items-center gap-2 px-2 sm:px-4 overflow-x-auto">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              const isEllipsis = crumb.label === "…"

              return (
                <div key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                  {index > 0 && <BreadcrumbSeparator />}
                  {isEllipsis ? (
                    <BreadcrumbItem>
                      <BreadcrumbEllipsis />
                    </BreadcrumbItem>
                  ) : isLast || !crumb.path ? (
                    <BreadcrumbItem>
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    </BreadcrumbItem>
                  ) : (
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={crumb.path}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  )}
                </div>
              )
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </header>
  )
}
